/**
 * The one place the HTTP conventions live (MCK-20260521-002, "The API contract is
 * generated and committed"). The generated types under `generated/` carry the shape of
 * every endpoint; this file carries everything the shape does not say:
 *
 *   - JSON only. `backend/src/kalqa/core/middleware.py` rejects form content types with a
 *     415 on every state-changing request, so a form POST is not a fallback — it is a bug.
 *   - Double-submit CSRF: the `kalqa_csrf` cookie is echoed as `X-Kalqa-CSRF` on unsafe
 *     methods. The cookie is minted by the backend on any safe request, so loading the app
 *     bootstraps it; there is no token endpoint to call.
 *   - `credentials: 'same-origin'`. The bundle and `/api/*` are served from one origin
 *     (`app.kalqa.app`), which is what makes `__Host-` cookies possible and CORS absent.
 *   - RFC 9457 `application/problem+json` parsed into one typed error, so callers branch
 *     on `problem.code` and never on the status code.
 *   - One `POST /auth/refresh` retry on a `401`, here rather than at any call site. An
 *     access cookie outlives its usefulness on a timer, so *every* authenticated call can
 *     meet a `401` that a rotation would fix; leaving that to call sites means the ones
 *     nobody thought about sign the user out mid-sentence.
 */

import createClient, { type Middleware } from 'openapi-fetch';

import type { components, paths } from './generated/openapi';

/** Cookie the backend mints on safe requests. `core/middleware.py:CSRF_COOKIE_NAME`. */
export const CSRF_COOKIE_NAME = 'kalqa_csrf';
/**
 * Which of the caller's companies a request is acting as.
 *
 * TWIN: `ACTIVE_WORKSPACE_HEADER` in `backend/src/kalqa/core/tenancy.py`. Renaming one
 * renames both.
 */
export const WORKSPACE_HEADER_NAME = 'X-Kalqa-Workspace';
/** Header the backend compares it against. `core/middleware.py:CSRF_HEADER_NAME`. */
export const CSRF_HEADER_NAME = 'X-Kalqa-CSRF';

const PROBLEM_MEDIA_TYPE = 'application/problem+json';

/** TWIN: `SAFE_METHODS` in `backend/src/kalqa/core/middleware.py`. Changing one changes both. */
const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/**
 * The error envelope, from the contract rather than from memory.
 *
 * These three were hand-written here until 2026-08-21, and they had to be: `openapi.json`
 * documented no `Problem` schema, no `code` enum and not one `application/problem+json`
 * response, so the generated client knew nothing about the shape the server has always sent.
 * `backend/src/kalqa/core/openapi.py` fixed the document; these are now aliases, which means
 * a code added to `ErrorCode` in the backend reaches every `error.code === '…'` comparison in
 * this app on the next `just gen-openapi && just gen-client` instead of silently not.
 *
 * `Problem` carries an index signature because the envelope is open: `trust_quota_exceeded`
 * names its budget, `stale_resource` carries `current` (`MCK-20260812-006` C).
 */
/** One validation failure, addressed by JSON Pointer — `core/errors.py:FieldError`. */
export type FieldError = components['schemas']['FieldError'];
/** The registered code enum. Branch on this, never on `type` or on `status`. */
export type ErrorCode = components['schemas']['ErrorCode'];
/** The RFC 9457 body as the backend emits it — `core/errors.py:Problem`. */
export type Problem = components['schemas']['Problem'];

/**
 * Every failed request reaches the caller as this — including the ones that never produced
 * a problem body (a proxy 502, a dropped connection), which get a synthesised one so no
 * call site has to handle two error shapes.
 */
export class ApiError extends Error {
  readonly problem: Problem;

  /**
   * How long the server asked the caller to wait, in seconds. `null` unless it said.
   *
   * **A header and not a body field**, which is why it is carried here rather than read off
   * `problem`: `core/errors.py:RateLimited` sets `Retry-After` and puts nothing in the
   * document. A screen that wants to say *when* rather than merely *too many* has no other
   * source, and re-deriving it per feature would mean every `problems.ts` reaching for a
   * `Response` that `unwrap` has already thrown away.
   */
  readonly retryAfterSeconds: number | null;

  constructor(problem: Problem, retryAfterSeconds: number | null = null) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Typed against the registered enum, so a mistyped comparison is a compile error. */
  get code(): ErrorCode {
    return this.problem.code;
  }

  get status(): number {
    return this.problem.status;
  }

  /** Field errors keyed by JSON Pointer, for binding a form's inline messages. */
  fieldErrors(): ReadonlyMap<string, FieldError> {
    return new Map((this.problem.errors ?? []).map((error) => [error.field, error]));
  }
}

function readCookie(name: string): string | null {
  // `document.cookie` is the only reader available: the session cookies are `HttpOnly`,
  // and the CSRF cookie is deliberately not — echoing it is the whole mechanism.
  for (const chunk of document.cookie.split(';')) {
    const [key, ...rest] = chunk.trim().split('=');
    if (key === name && rest.length > 0) return rest.join('=');
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A body that is not a well-formed problem still has to arrive as one. */
function synthesiseProblem(status: number, statusText: string): Problem {
  return {
    type: 'about:blank',
    title: statusText || 'Request failed',
    status,
    code: status >= 500 ? 'internal_error' : 'bad_request',
  };
}

/**
 * The problem body, from the value openapi-fetch **already read** — never from the response.
 *
 * This function used to take only the `Response` and read it with `response.clone().json()`,
 * and that could never work: on any non-`ok` answer openapi-fetch consumes the body itself
 * (`let error = await response.text()`, then `JSON.parse`), and a `Response` whose body has
 * been read throws `TypeError` from `clone()`. The throw landed in this function's own
 * `catch`, so every failure in the app arrived as a *synthesised* problem — `code` was always
 * `bad_request` or `internal_error`, and `detail` was always absent.
 *
 * That made every per-feature `problems.ts` in this repo dead: a `409` whose sentence names
 * the way forward came out as "Request failed", and a `cursor_invalid` was indistinguishable
 * from a `403`. Found 2026-08-21 while building the tasks screen, whose board renders the
 * server's own `409` sentence at the card and therefore cannot work without this.
 *
 * `body` is what openapi-fetch parsed: the decoded object when the body was JSON, the raw
 * string when it was not, `undefined` on a `204`. Only the first shape is a problem.
 */
function toProblem(response: Response, body: unknown): Problem {
  const fallback = synthesiseProblem(response.status, response.statusText);
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.startsWith(PROBLEM_MEDIA_TYPE) && !contentType.includes('json')) {
    return fallback;
  }
  if (!isRecord(body) || typeof body.code !== 'string') return fallback;
  return { ...fallback, ...body };
}

/**
 * `Retry-After` as a number of seconds, or `null`.
 *
 * Only the delta-seconds form is read. RFC 9110 also allows an HTTP-date, and Kalqa never
 * sends one — `RateLimited` writes `str(retry_after_seconds)` and nothing else — so parsing a
 * date here would be handling a case that cannot arrive, and handling it *wrongly* would be a
 * clock-skew bug nobody could reproduce. An unreadable value is `null`, which reads on screen
 * as "too many attempts" without a window rather than as "try again in NaN minutes".
 */
function retryAfterOf(response: Response): number | null {
  const header = response.headers.get('Retry-After');
  if (header === null) return null;
  const seconds = Number(header.trim());
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/**
 * The safe request that mints the CSRF cookie when a browser arrives without one.
 *
 * `CsrfMiddleware` in `backend/src/kalqa/core/middleware.py` mints on any safe request and
 * has no token endpoint, on the reasoning that "a fresh browser bootstraps a token by
 * loading the app". That reasoning holds for every screen except the first one: on
 * `/sign-in` the *first* call a new visitor makes is a `POST` — start SSO, ask for a
 * sign-in link, sign in — and it would be refused for a missing token the visitor had no
 * way to have. So the client asks for one first.
 *
 * `/auth/me` because it is safe, cheap, already in the contract and already proxied. Its
 * `401` for an anonymous caller is expected and discarded — the cookie is set on the
 * response regardless of status.
 */
const CSRF_BOOTSTRAP_PATH = '/api/v1/auth/me';

/** One bootstrap at a time: a screen firing two mutations must not mint two tokens. */
let bootstrap: Promise<void> | null = null;

async function ensureCsrfCookie(): Promise<string | null> {
  const existing = readCookie(CSRF_COOKIE_NAME);
  if (existing !== null) return existing;

  bootstrap ??= (async () => {
    try {
      // A plain `fetch` and not `api.GET`, deliberately: routing this through the client
      // would re-enter this middleware and hand the 401 to the refresh handler, turning one
      // bootstrap into three requests.
      await fetch(CSRF_BOOTSTRAP_PATH, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
    } catch {
      // Offline, or the API is down. The real request is about to fail on its own terms and
      // say something more useful than this one could.
    } finally {
      bootstrap = null;
    }
  })();

  await bootstrap;
  return readCookie(CSRF_COOKIE_NAME);
}

/**
 * The workspace every request acts as, held for the lifetime of this tab.
 *
 * **This is not the `localStorage` design `KLQ-20260819-007` §3 rejected**, and the
 * difference is the whole reason it is allowed to exist. That design was rejected because
 * two tabs share one `localStorage`: a person reviewing studio A in one tab and switching to
 * studio B in another would have a first tab that still *displayed* A while every write from
 * it landed in B. This variable is module scope, so it is per tab, and it is never
 * persisted — each tab reloads with nothing and derives its value again.
 *
 * It is **derived, not chosen here**: `RequireSession` sets it from the route parameter of a
 * workspace-scoped URL, or from the server's own answer when the screen is not one. The URL
 * remains the source of truth; this is the courier that carries it to a `fetch` running
 * outside React.
 *
 * Every workspace-scoped query key carries the same id (`features/studio`,
 * `features/settings`), so a switch cannot serve one company's data under another's heading
 * from cache — the header and the key move together or the cache would lie.
 */
let activeWorkspaceId: string | null = null;

export function setActiveWorkspace(workspaceId: string | null): void {
  activeWorkspaceId = workspaceId;
}

export function getActiveWorkspace(): string | null {
  return activeWorkspaceId;
}

/**
 * Names the acting company on every request.
 *
 * On safe requests too, and that is deliberate: `GET /workspace/members` answers a different
 * list per company, so a read that omitted the header would show studio A's people under
 * studio B's heading. The server treats an absent header as "the home workspace", which is
 * what keeps every call made before this existed working unchanged.
 */
const workspaceMiddleware: Middleware = {
  onRequest({ request }) {
    // **An explicit header on the call wins.** The tab's value is a default, and one call
    // site genuinely needs to override it: the new-project drawer files a project under a
    // company the person picked in the form, which may not be the one the chrome is showing.
    if (activeWorkspaceId === null || request.headers.has(WORKSPACE_HEADER_NAME)) return request;
    request.headers.set(WORKSPACE_HEADER_NAME, activeWorkspaceId);
    return request;
  },
};

const csrfMiddleware: Middleware = {
  async onRequest({ request }) {
    if (SAFE_METHODS.has(request.method.toUpperCase())) return undefined;
    const token = await ensureCsrfCookie();
    if (token !== null) request.headers.set(CSRF_HEADER_NAME, token);
    return request;
  },
};

/**
 * The generated-types client. Prefer the `request` helper below at call sites: it turns the
 * `{ data, error }` pair into a value-or-throw, which is what TanStack Query wants.
 */
export const api = createClient<paths>({
  // Same-origin: the app container serves the bundle and proxies `/api/*`.
  baseUrl: '/',
  credentials: 'same-origin',
  headers: { Accept: 'application/json' },
});

/** `POST /api/v1/auth/refresh` — the rotation the 401 handler below performs. */
const REFRESH_PATH = '/api/v1/auth/refresh';

/**
 * Routes where a `401` is the answer rather than an expired session, so a refresh would be
 * a wasted round trip that cannot change the outcome.
 *
 * Every one of them is anonymous by design — they are how a caller *becomes* authenticated
 * (`AllowAnonymous` in `backend/src/kalqa/domains/auth/router.py`). TWIN: `PUBLIC_PATHS` in
 * `backend/tests/contract/test_route_auth_coverage.py` is the same list from the server's
 * side; a route added there is a route to add here.
 */
const NO_REFRESH_PATHS: ReadonlySet<string> = new Set([
  REFRESH_PATH,
  '/api/v1/auth/sign-in',
  '/api/v1/auth/sign-up',
  '/api/v1/auth/verify-email',
  '/api/v1/auth/magic-link',
  '/api/v1/auth/magic-link-sign-in',
  '/api/v1/auth/oidc-start',
  '/api/v1/auth/oidc-callback',
  '/api/v1/auth/oidc-confirm-link',
  '/api/v1/invitations/preview',
  '/api/v1/invitations/accept',
  // The recipient's read surface (`KLQ-20260826-002` §B row 3). These are the one group here
  // that is not a door — nobody becomes authenticated through them — and they are on the list
  // for the stronger reason: the share surface must make **no authenticated call at all**. A
  // rotation fired from a page opened by a stranger would refresh whichever account happens to
  // be signed in on that browser, which is the silent identity switch this feature is written
  // to avoid. The credential is the share cookie, and no `POST /auth/refresh` can renew it.
  '/api/v1/shared/entity',
  '/api/v1/shared/rendition',
  '/api/v1/shared/tiles',
  '/api/v1/shared/download',
]);

/**
 * The replay copy of each in-flight refreshable request, so a `401` can be retried after the
 * rotation. Cloning has to happen before the request is sent — a `Request` whose body has
 * been read cannot be sent twice — which is why this is captured in `onRequest` and not
 * reconstructed in `onResponse`.
 *
 * **Only a request with a body is cloned.** A bodyless `Request` has no stream to consume, so
 * the same object is handed to `fetch` twice and the spec is content — and a `GET` is most of
 * what goes through here. The bodies that *are* cloned are all JSON: file bytes never pass
 * this client (`features/drawings/upload.ts` sends them by `XMLHttpRequest` to a presigned
 * URL), so the "two copies of a multipart upload in memory" the review feared (2026-09-25
 * §4.14) cannot happen here, and the largest body a clone ever holds is a markup's geometry.
 */
const retryable = new Map<string, Request>();

/** One rotation at a time: ten screens hitting a stale cookie must not send ten refreshes. */
let rotation: Promise<boolean> | null = null;

async function rotateOnce(): Promise<boolean> {
  rotation ??= (async () => {
    try {
      const { response } = await api.POST(REFRESH_PATH);
      return response.ok;
    } catch {
      // A network failure is not a signed-out user. The caller still sees its own 401 and
      // the guard decides; retrying the original request here would only repeat the failure.
      return false;
    } finally {
      rotation = null;
    }
  })();
  return rotation;
}

/**
 * The 401 handler. Exactly one refresh, exactly one retry: a loop here is how a browser
 * hammers an API it has no credentials for, and a second 401 is the honest answer that
 * `features/session` turns into a trip to `/login`.
 */
const refreshMiddleware: Middleware = {
  onRequest({ request, id }) {
    if (!NO_REFRESH_PATHS.has(new URL(request.url).pathname)) {
      retryable.set(id, request.body === null ? request : request.clone());
    }
    return request;
  },
  async onResponse({ response, id }) {
    const original = retryable.get(id);
    retryable.delete(id);
    if (response.status !== 401 || original === undefined) return response;
    if (!(await rotateOnce())) return response;
    return fetch(original);
  },
  onError({ id }) {
    // A request that never produced a response never reaches `onResponse`; without this the
    // clone would sit in the map for the life of the tab.
    retryable.delete(id);
    return undefined;
  },
};

// Order matters. `onRequest` runs in registration order, so the CSRF header is on the
// request *before* the refresh middleware clones it — a retry that lost the header would
// come back 403 and look like a permission problem.
api.use(csrfMiddleware);
// Before the refresh middleware, for the reason the CSRF header is: the retry replays a
// clone taken in `onRequest`, and a clone missing this header would be answered by a
// different company than the request the user made.
api.use(workspaceMiddleware);
api.use(refreshMiddleware);

/**
 * Normalises an openapi-fetch result into a value, throwing `ApiError` on failure.
 *
 * Callers hand it the promise a `api.GET(...)` / `api.POST(...)` call returns:
 *
 *     const me = await unwrap(api.GET('/api/v1/auth/me'));
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok) throw new ApiError(toProblem(response, error), retryAfterOf(response));
  // A 204 carries no body; the generated type says `never`, and `undefined` is the value.
  return data as T;
}

/**
 * The same call, keeping the validator the response carried.
 *
 * A second function rather than a changed signature on `unwrap`: fifty call sites do not
 * care about an ETag, and making all of them destructure a pair to serve the handful of
 * guarded endpoints is the tail wagging the dog (`KLQ-20260819-008` §1).
 *
 * **Without this, `PATCH /workspace` is unreachable rather than merely unused.** It calls
 * `require_if_match`, which answers `428` when the header is absent — and until now nothing
 * in this app could produce one, because `unwrap` reads `data` and throws the `Response`
 * away. Every guarded endpoint after it needs the same reader (`MCK-20260812-006` K names
 * `PATCH /workspace` as the first of several).
 *
 * `etag` is `null` when the endpoint sent none, and a caller holding `null` must not invent
 * a value: `If-Match: *` would match anything, which is precisely the last-writer-wins the
 * header exists to prevent.
 */
export interface Representation<T> {
  readonly data: T;
  readonly etag: string | null;
}

export async function unwrapWithETag<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<Representation<T>> {
  const { data, error, response } = await call;
  if (!response.ok) throw new ApiError(toProblem(response, error), retryAfterOf(response));
  return { data: data as T, etag: response.headers.get('ETag') };
}

/**
 * The same call, for an endpoint whose body is a **file** rather than JSON.
 *
 * A third reader for the same reason there is a second one: the CSV export answers
 * `text/csv`, so `openapi.json` documents no `200` content for it and the generated type says
 * `never` — reading it as `data` would be a lie the compiler would have to be talked past. The
 * caller passes `parseAs: 'blob'` and gets the blob; the failure path is unchanged, so a `403`
 * on an export is the same `ApiError` as a `403` on the list it was exported from.
 */
export async function unwrapFile(
  call: Promise<{ data?: unknown; error?: unknown; response: Response }>,
): Promise<Blob> {
  const { data, error, response } = await call;
  if (!response.ok) throw new ApiError(toProblem(response, error), retryAfterOf(response));
  return data as Blob;
}
